import { useMemo } from 'react';
import { NavLink, useLocation } from 'react-router';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Minus, Star } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { toast } from '@/components/ui';
import { useAuthContext } from '@/providers/AuthProvider';
import { isMenuAllowed, menusOf } from '@/lib/menuAccess';
import { FAVORITE_MENU_MAX, useFavoriteMenusStore } from '@/stores/useFavoriteMenus';
import { FAVORITE_MENU_CANDIDATES, type FavoriteMenuCandidate } from './Sidebar';
import { FavoriteMenuPicker } from './FavoriteMenuPicker';

/** 지금 계정이 볼 수 있는 후보 + 그 계정의 즐겨찾기(권한이 없어졌거나 사라진 메뉴는 뺀다). */
const useFavoriteMenus = () => {
  const { currentAdmin } = useAuthContext();
  const adminId = currentAdmin?.adminUserId ?? '';
  const roleKey = (currentAdmin?.roles ?? []).join(',');
  // QA-514 역할별 접근 메뉴 — 숨긴 메뉴는 즐겨찾기에도 안 보인다.
  const menuKey = JSON.stringify(menusOf(currentAdmin));
  const saved = useFavoriteMenusStore((s) => (adminId ? s.byAdmin[adminId] : undefined));
  const candidates = useMemo(() => {
    const roles = roleKey.split(',');
    const menus = JSON.parse(menuKey) as string[] | null;
    return FAVORITE_MENU_CANDIDATES.filter((c) => c.required.some((r) => roles.includes(r)) && isMenuAllowed(menus, c.to));
  }, [roleKey, menuKey]);
  const favorites = useMemo(() => {
    const byTo = new Map(candidates.map((c) => [c.to, c]));
    return (saved ?? []).map((to) => byTo.get(to)).filter((c): c is FavoriteMenuCandidate => c !== undefined);
  }, [saved, candidates]);
  return { adminId, candidates, favorites };
};

const confirmRemove = (label: string) => window.confirm(`'${label}'을(를) 즐겨찾는 메뉴에서 삭제할까요?`);

const FavoriteChip = ({ item, label, onRemove }: { item: FavoriteMenuCandidate; label: string; onRemove: () => void }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.to });
  const Icon = item.icon;
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1, zIndex: isDragging ? 1 : undefined }}
      className="group relative shrink-0"
      data-testid={`favorite-menu-${item.to}`}
    >
      <NavLink
        to={item.to}
        end
        {...attributes}
        {...listeners}
        className="flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-[12px] font-medium no-underline outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]"
        style={({ isActive }) => ({
          border: `1px solid ${isActive ? 'var(--accent-default)' : 'var(--divider-strong)'}`,
          color: isActive ? 'var(--accent-default)' : 'var(--content-default)',
          background: 'var(--surface-0)',
          cursor: isDragging ? 'grabbing' : 'pointer',
        })}
        title="끌어서 순서를 바꿀 수 있습니다"
      >
        <Icon size={13} aria-hidden="true" />
        {label}
      </NavLink>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`${label} 즐겨찾기 삭제`}
        className="absolute -right-1 -top-1 hidden h-4 w-4 items-center justify-center rounded-full text-white group-focus-within:flex group-hover:flex"
        style={{ background: 'var(--content-muted)' }}
        data-testid={`favorite-menu-remove-${item.to}`}
      >
        <Minus size={10} aria-hidden="true" />
      </button>
    </li>
  );
};

/**
 * QA-509 상단 검색바 오른쪽 — 즐겨찾는 메뉴(계정별 최대 7개). [+]를 누르면 사이드바처럼 묶인 메뉴 체크박스(메뉴 검색 포함)에서
 * 고르고 푼다. 칩의 [−]는 확인 후 삭제, 칩을 끌어 순서 변경. 이 관리자가 볼 수 있는 메뉴만 나온다.
 */
export const FavoriteMenuBar = () => {
  const { t } = useTranslation('common');
  const { adminId, candidates, favorites } = useFavoriteMenus();
  const add = useFavoriteMenusStore((s) => s.add);
  const remove = useFavoriteMenusStore((s) => s.remove);
  const reorder = useFavoriteMenusStore((s) => s.reorder);
  // 클릭(이동)과 끌기를 나누려고 조금 움직여야 끌기가 시작된다.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  if (!adminId) return null;

  const labelOf = (c: FavoriteMenuCandidate) => String(t(c.labelKey));

  const onDragEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const ids = favorites.map((f) => f.to);
    reorder(adminId, arrayMove(ids, ids.indexOf(String(e.active.id)), ids.indexOf(String(e.over.id))));
  };

  return (
    <nav aria-label="즐겨찾는 메뉴" className="hidden min-w-0 items-center gap-2 lg:flex" data-testid="favorite-menu-bar">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={favorites.map((f) => f.to)} strategy={horizontalListSortingStrategy}>
          <ul className="flex min-w-0 items-center gap-2 overflow-x-auto px-1 py-1">
            {favorites.map((f) => (
              <FavoriteChip
                key={f.to}
                item={f}
                label={labelOf(f)}
                onRemove={() => {
                  if (confirmRemove(labelOf(f))) remove(adminId, f.to);
                }}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
      <FavoriteMenuPicker
        candidates={candidates}
        selected={favorites.map((f) => f.to)}
        onToggle={(to, on) => (on ? add(adminId, to) : remove(adminId, to))}
      />
    </nav>
  );
};

/** QA-509 페이지 제목 옆 별 — 이 화면(사이드바 메뉴와 같은 주소일 때)을 즐겨찾는 메뉴에 넣고 뺀다. */
export const FavoriteStar = () => {
  const { pathname } = useLocation();
  const { t } = useTranslation('common');
  const { adminId, candidates, favorites } = useFavoriteMenus();
  const add = useFavoriteMenusStore((s) => s.add);
  const remove = useFavoriteMenusStore((s) => s.remove);
  const here = candidates.find((c) => c.to === pathname);
  if (!adminId || !here) return null;
  const label = String(t(here.labelKey));
  const on = favorites.some((f) => f.to === here.to);
  const onClick = () => {
    if (on) {
      if (confirmRemove(label)) remove(adminId, here.to);
      return;
    }
    if (!add(adminId, here.to)) {
      toast.error(`즐겨찾는 메뉴는 ${FAVORITE_MENU_MAX}개까지입니다. 위쪽 메뉴에서 하나를 지운 뒤 추가하세요.`);
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      aria-label={on ? `${label} 즐겨찾기 삭제` : `${label} 즐겨찾기 추가`}
      title={on ? '즐겨찾는 메뉴에서 빼기' : '즐겨찾는 메뉴에 넣기'}
      className="flex h-7 w-7 items-center justify-center rounded-md outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]"
      data-testid="favorite-star"
    >
      <Star size={18} aria-hidden="true" style={{ color: on ? '#F5B301' : 'var(--content-soft)', fill: on ? '#F5B301' : 'none' }} />
    </button>
  );
};
